import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { debounceTime, distinctUntilChanged, map, Observable, startWith, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { MaterialModule } from 'src/app/material.module';
import { LookupService } from 'src/app/services/lookup.service';
import { DrugService } from '../drug.service';
import { ToastrService } from 'ngx-toastr';
import { DrugCompanyDetailComponent } from '../../drug-company/drug-company-detail/drug-company-detail.component';
import { GenericContentDetailComponent } from '../../generic-content-list/generic-content-detail/generic-content-detail.component';
import { IDrugCompany } from '../../drug-company/idrug-company';
import { IDrug } from '../idrug';
import { IDrugType } from '../../drug-type/idrug-type';
import { IGenericContent } from '../../generic-content-list/igeneric-content';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';

@Component({
  selector: 'app-drug-detail',
  imports: [
    CommonModule,
    MaterialModule,
    TablerIconsModule,
    TranslateModule,
    ReactiveFormsModule
  ],
  templateUrl: './drug-detail.component.html',
  styleUrl: './drug-detail.component.scss'
})
export class DrugDetailComponent implements OnInit {
  drugForm: FormGroup;
  isEdit: boolean;

  DrugType: CommonDropDown[] = [];
  drugTypeCtrl = new FormControl();
  filteredDrugType$: Observable<CommonDropDown[]>;
  showAddOptionfortype = false;

  DrugCompany: CommonDropDown[] = [];
  filteredDrugCompany$: Observable<CommonDropDown[]>;
  drugcompanyCtrl = new FormControl();
  showAddOptionforcompany = false;

  genericContent: CommonDropDown[] = [];
  filteredGenericContent$: Observable<CommonDropDown[]>;
  genericcontentCtrl = new FormControl();
  showAddOptionforgenricContent = false;
  selectedGenericNames: string[] = [];
  selectedGenericContentIds: number[] = [];
  selectedGenerics: CommonDropDown[] = [];

  selectedDrugTypeId: number | null = null;
  selectedDrugcompanyId: number | null = null;
  private _unsubscribeAll: Subject<void> = new Subject<void>();

  constructor(
    private _formbuilder: FormBuilder,
    private dialogRef: MatDialogRef<DrugDetailComponent>,
    private translate: TranslateService,
    private _lookupService: LookupService,
    private _drugService: DrugService,
    private toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) private _data: { drug: IDrug, isEdit: boolean },
    private dialog: MatDialog,
    private translationUtils: TranslationUtils
  ) {
    this.isEdit = _data?.isEdit;

  }

  ngOnInit(): void {

    this.drugForm = this._formbuilder.group({
      name: [this._data?.drug?.name, [Validators.required, Validators.maxLength(50)]],
      genericName: [this._data?.drug?.genericName, Validators.maxLength(50)],
      typeId: [this._data?.drug?.typeId, Validators.required],
      companyId: [this._data?.drug?.companyId],
      genericContentIds: [[] as number[]],
      instructions: [this._data?.drug?.instructions, Validators.maxLength(255)],
    });
    this.drugTypeCtrl = new FormControl(this._data?.drug?.typeId || null, Validators.required);

    this.filteredDrugType$ = this.drugTypeCtrl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => {
        const val = typeof value === 'string' ? value : value?.name || '';

        return this._lookupService.getDrugType(val).pipe(
          tap(list => {
            this.showAddOptionfortype =
              val && !list.some(x => x.name.toLowerCase() === val.toLowerCase());
          })
        );
      })
    );
    if (this.isEdit && this._data?.drug?.typeId) {
      this._lookupService.getDrugType().subscribe(list => {
        const match = list.find(x => x.id === this._data.drug.typeId);
        if (match) this.drugTypeCtrl.setValue(match);
      });
    }

    this.filteredDrugCompany$ = this.drugcompanyCtrl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => {
        const val = typeof value === 'string' ? value : value?.name || '';

        return this._lookupService.getDrugCompany(val).pipe(
          tap(list => {
            this.showAddOptionforcompany =
              val && !list.some(x => x.name.toLowerCase() === val.toLowerCase());
          })
        );
      })
    );
    if (this.isEdit && this._data?.drug?.companyId) {
      this._lookupService.getDrugCompany().subscribe(list => {
        const match = list.find(x => x.id === this._data.drug.companyId);
        if (match) {
          this.drugcompanyCtrl.setValue(match);
        }
      });
    }

    this._lookupService.getGenericContent()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.genericContent = data || [];

        this.filteredGenericContent$ = this.genericcontentCtrl.valueChanges.pipe(
          startWith(''),
          debounceTime(300),
          map((value) => {
            const val = typeof value === 'string' ? value : value?.name || '';
            const filtered = this._filterGenericContent(val);
            this.showAddOptionforgenricContent =
              val && !filtered.some((x) => x.name.toLowerCase() === val.toLowerCase());
            return filtered;
          })
        );

        // Watch textbox manually edited to sync checkboxes
        this.genericcontentCtrl.valueChanges.subscribe((value) => {
          if (typeof value === 'string') {
            this.syncSelectionWithText(value);
          }
        });
        if (this.isEdit && this._data?.drug.genericContents?.length) {
          const selectedGenerics = this.genericContent.filter(gc =>
            this._data.drug.genericContents?.some((dgc: any) => dgc.id === gc.id)
          );

          this.selectedGenerics = selectedGenerics;
          this.selectedGenericContentIds = selectedGenerics.map(g => g.id);
          this.selectedGenericNames = selectedGenerics.map(g => g.name);

          // Update textbox & form
          this.genericcontentCtrl.setValue(this.selectedGenericNames.join(', '), { emitEvent: false });
          this.drugForm.patchValue({
            genericContentIds: this.selectedGenericContentIds
          });
        }
      });
  }

  private _filterGenericContent(value: string) {
    const filterValue = value.toLowerCase();
    return this.genericContent.filter((option) =>
      option.name.toLowerCase().includes(filterValue)
    );
  }

  toggleSelection(event: any, generic: IGenericContent): void {
    if (event.checked) {
      // Add selected
      this.selectedGenericContentIds.push(generic.id);
      this.selectedGenericNames.push(generic.name);
    } else {
      this.selectedGenericContentIds = this.selectedGenericContentIds.filter(id => id !== generic.id);
      this.selectedGenericNames = this.selectedGenericNames.filter(name => name !== generic.name);
    }

    this.drugForm.patchValue({
      genericContentIds: this.selectedGenericContentIds
    });

    this.genericcontentCtrl.setValue(this.selectedGenericNames.join(', '), { emitEvent: false });
  }

  isSelected(generic: IGenericContent): boolean {
    return this.selectedGenericContentIds.includes(generic.id);
  }


  updateTextboxFromSelection() {
    this.selectedGenericNames = this.selectedGenerics.map((g) => g.name);
    this.genericcontentCtrl.setValue(this.selectedGenericNames.join(', '), {
      emitEvent: false, // avoid infinite loop
    });
  }

  syncSelectionWithText(value: string) {
    const typedNames = value
      .split(',')
      .map((v) => v.trim())
      .filter((v) => !!v);

    // Update selectedGenerics array
    this.selectedGenerics = this.genericContent.filter((g) =>
      typedNames.includes(g.name)
    );

    // Update checkboxes in dropdown
    this.selectedGenericNames = this.selectedGenerics.map((g) => g.name);
  }


  displayDrugType(drugType: IDrugType): string {
    return drugType ? drugType.name : '';
  }

  displayDrugcompany(company: IDrugCompany): string {
    return company && company.name ? company.name : '';
  }

  displayGenericContent(): string {
    return this.selectedGenericNames ? this.selectedGenericNames.join(', ') : '';
  }

  onSaveDrug() {
    const data = this.drugForm.getRawValue();
    this._drugService.createDrugs(data).subscribe((response) => {
      if (response.isSuccess) {
        this.dialogRef.close(response);
        this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.CREATE', this.translate.instant('ENTITY.DRUG')));
      }
      else {
        const allErrors = Object.values(response.errors).flat();
        this.toastr.error(allErrors.join("\n"));
      }
    });
  }

  onUpdateDrug() {
    const data = {
      id: this._data?.drug?.id,
      ...this.drugForm.getRawValue()
    };

    this._drugService.updateDrug(data).subscribe((response) => {

      if (response.isSuccess) {
        this.dialogRef.close(response);
        this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.UPDATE', this.translate.instant('ENTITY.DRUG')));
      } else {
        const allErrors = Object.values(response.errors).flat();
        this.toastr.error(allErrors.join("\n"));
      }
    });
  }

  onCloseDialog(): void {
    this.dialogRef.close();
  }

  onDrugTypeSelected(drugType: IDrugType) {
    this.selectedDrugTypeId = drugType.id;

    this.drugForm.patchValue({
      typeId: drugType.id
    });

    this.drugTypeCtrl.setValue(drugType);
  }

  onDrugCompanySelected(drugcompany: IDrugCompany) {
    this.selectedDrugcompanyId = drugcompany.id;

    this.drugForm.patchValue({
      companyId: drugcompany.id
    });

    this.drugcompanyCtrl.setValue(drugcompany);
  }

  addDrugCompany(drugcompanyCtrl: string) {
    this.dialog.open(DrugCompanyDetailComponent, {
      width: '500px',
      height: 'auto',
      data: {
        companyValue: drugcompanyCtrl
      }
    }).afterClosed().subscribe(response => {
      if (!response) return;

      // Only call the API once after adding
      this._lookupService.getDrugCompany().subscribe(list => {

        const added = list.find(c =>
          c.id === response.result.id ||
          c.name?.toLowerCase() === response.result.name?.toLowerCase()
        );

        if (added) {
          this.drugcompanyCtrl.setValue(added);
          this.drugForm.patchValue({ companyId: added.id });
        }
      });
    })

  }

  addGenericContent() {
    const dialogRef = this.dialog.open(GenericContentDetailComponent, {
      width: '500px',
      height: 'auto',
    });

    dialogRef.afterClosed().subscribe((response) => {
      if (response) {
        // Refresh the list from API
        this._lookupService.genericcontents$
          .pipe(takeUntil(this._unsubscribeAll))
          .subscribe((data) => {
            this.genericContent = data || [];

            this.filteredGenericContent$ = this.genericcontentCtrl.valueChanges.pipe(
              startWith(''),
              debounceTime(300),
              map((value) => {
                const val = typeof value === 'string' ? value : value?.name || '';
                const filtered = this._filterGenericContent(val);
                this.showAddOptionforgenricContent =
                  val && !filtered.some((x) => x.name.toLowerCase() === val.toLowerCase());
                return filtered;
              })
            );

            // Find the newly added item
            const added = this.genericContent.find(gc =>
              gc.id === response.result.id ||
              gc.name?.toLowerCase() === response.result.name?.toLowerCase()
            );

            if (added) {
              // Add to selection
              this.selectedGenericContentIds.push(added.id);
              this.selectedGenericNames.push(added.name);
              this.selectedGenerics.push(added);

              //Update form & textbox
              this.drugForm.patchValue({
                genericContentIds: this.selectedGenericContentIds
              });
              this.genericcontentCtrl.setValue(this.selectedGenericNames.join(', '), { emitEvent: false });
            }
          });
      }
    });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

}